import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DynamicDialogRef } from 'primeng/dynamicdialog';

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

import { InventoryService } from '../../../services/inventory.service';
import { CourseService } from '../../../services/course.service';
import { UserService } from '../../../services/user.service';
import { NotificationService } from '../../../services/notification.service';
import {
  CategoryKind,
  parseCategoryKind,
} from '../../../utils/inventory-category-kind';

import * as ExcelJS from 'exceljs';

type CategoryOpt = { id: number; name: string; kind: CategoryKind };
type StatusOpt = { code: string; name: string };
type CourseOpt = { courseId: number; courseName: string };
type ParticipantOpt = { id: number; fullName: string };

type ImportRowDE = {
  Kategorie?: string;
  Kurs?: string;
  Teilnehmer?: string;

  Modell?: string;
  Seriennummer?: string;

  CPU?: string;
  Hersteller?: string;
  RAM?: string;
  Speicher?: string;
  Betriebssystem?: string;

  'Buch Versand'?: string; // "Versendet" | "Nicht versendet"

  Startdatum?: string;
  Enddatum?: string;

  Standort?: string;
  'Asset-Tag'?: string;

  Status?: string;
  Rückgabestatus?: string;

  'Admin-Benutzername'?: string;
  'Admin-Passwort'?: string;
  Passwort?: string;

  Anmerkungen?: string;
};
type ImportRowDEWithMeta = ImportRowDE & { __rowNo: number };

// Spiegelt CreateInventoryDTO im Backend - Felder, die es dort nicht gibt,
// werden vom Server stillschweigend verworfen.
type CreateInventoryDto = {
  categoryId: number;
  courseId: number | null;
  participantId: number | null;

  model?: string | null;
  serialNumber?: string | null;

  cpu?: string | null;
  hersteller?: string | null;
  ram?: string | null;
  speicher?: string | null;
  betriebssystem?: string | null;

  buchVersandStatus?: string | null;

  startDate?: string | null;
  endDate?: string | null;

  location?: string | null;
  assetTag?: string | null;
  status?: string | null;
  returned?: boolean;

  adminBenutzername?: string | null;
  adminPasswort?: string | null;
  passwort?: string | null;

  note?: string | null;
};

@Component({
  selector: 'app-inventory-import-dialog',
  standalone: true,
  imports: [FormsModule, DialogModule, ButtonModule, TableModule],
  templateUrl: './inventory-import-dialog.component.html',
  styleUrl: './inventory-import-dialog.component.scss',
})
export class InventoryImportDialogComponent {
  processingFile = false;
  selectedFile: File | null = null;

  get canClickImport(): boolean {
    return (
      !this.processingFile &&
      !!this.selectedFile &&
      !this.validationErrors.length &&
      !!this.payload.length
    );
  }

  existingSerials: string[] = [];

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  isDragOver = false;

  fileName: string | null = null;

  excelRows: ImportRowDEWithMeta[] = [];
  payload: CreateInventoryDto[] = [];

  validationErrors: string[] = [];
  importing = false;
  loadingTemplate = false;

  categories: CategoryOpt[] = [];
  statuses: StatusOpt[] = [];
  courses: CourseOpt[] = [];
  participants: ParticipantOpt[] = [];

  constructor(
    private ref: DynamicDialogRef,
    private inventoryService: InventoryService,
    private courseService: CourseService,
    private userService: UserService,
    private notification: NotificationService,
  ) {}

  close(): void {
    this.ref.close(null);
  }

  triggerBrowse(): void {
    this.fileInput?.nativeElement?.click();
  }

  private isExampleRow(r: ImportRowDEWithMeta): boolean {
    return r.__rowNo === 3;
  }

  onDragOver(ev: DragEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(ev: DragEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(ev: DragEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.isDragOver = false;

    const file = ev.dataTransfer?.files?.[0] ?? null;
    if (!file) return;

    const name = (file.name ?? '').toLowerCase();
    const ok = name.endsWith('.xlsx') || name.endsWith('.xls');
    if (!ok) {
      this.selectedFile = null;
      this.fileName = null;
      this.payload = [];
      this.excelRows = [];
      this.validationErrors = [
        'Bitte eine Excel-Datei (.xlsx oder .xls) auswählen.',
      ];
      return;
    }

    this.handleFile(file);
  }

  onFileSelected(event: any): void {
    const file: File | null = event?.target?.files?.[0] ?? null;

    if (!file) {
      this.selectedFile = null;
      this.fileName = null;
      this.payload = [];
      this.excelRows = [];
      this.validationErrors = [];
      return;
    }

    this.handleFile(file);

    if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = '';
  }

  // ==========================
  // TEMPLATE DOWNLOAD
  // ==========================
  async downloadTemplate(): Promise<void> {
    this.loadingTemplate = true;
    this.validationErrors = [];
    this.existingSerials = [];

    try {
      await this.loadListsFromDb();

      if (!this.categories.length) {
        this.validationErrors = ['Kategorieliste ist leer.'];
        return;
      }
      if (!this.courses.length) {
        this.validationErrors = ['Kursliste ist leer.'];
        return;
      }
      if (!this.participants.length) {
        this.validationErrors = ['Teilnehmerliste ist leer.'];
        return;
      }

      const headers = [
        'Kategorie', // A
        'Kurs', // B
        'Teilnehmer', // C
        'Modell', // D
        'Seriennummer', // E
        'CPU', // F
        'Hersteller', // G
        'RAM', // H
        'Speicher', // I
        'Betriebssystem', // J

        'Buch Versand', // K

        'Startdatum', // L
        'Enddatum', // M

        'Standort', // N
        'Asset-Tag', // O
        'Status', // P
        'Anmerkungen', // Q

        'Rückgabestatus', // R

        'Admin-Benutzername', // S
        'Admin-Passwort', // T
        'Passwort', // U
      ];

      const wb = new ExcelJS.Workbook();
      wb.creator = 'Inventar';
      wb.created = new Date();

      const ws = wb.addWorksheet('Inventar');

      const lastColLetter = ws.getColumn(headers.length).letter;
      ws.mergeCells(`A1:${lastColLetter}1`);

      const hintCell = ws.getCell('A1');
      hintCell.value =
        'HINWEIS: In den Spalten A (Kategorie), B (Kurs) und C (Teilnehmer) bitte per Dropdown auswählen. ' +
        'Zeile 3 ist ein Beispiel und wird nicht importiert.\n' +
        'Seriennummer: Falls eine Seriennummer bereits im System existiert, erscheint eine Warnung.\n' +
        'Buch Versand: Bitte "Versendet" oder "Nicht versendet" auswählen.';
      hintCell.alignment = {
        vertical: 'middle',
        horizontal: 'left',
        wrapText: true,
      };
      hintCell.font = { bold: true };
      hintCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF3F4F6' },
      };
      ws.getRow(1).height = 56;

      ws.addRow(headers);

      ws.getRow(2).font = { bold: true };
      ['A2', 'B2', 'C2'].forEach((addr) => {
        ws.getCell(addr).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE8F5E9' },
        };
      });

      ws.getCell('E2').fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFF3CD' },
      };

      ws.views = [{ state: 'frozen', ySplit: 2 }];

      ws.columns = [
        { width: 22 }, // A Kategorie
        { width: 28 }, // B Kurs
        { width: 30 }, // C Teilnehmer
        { width: 18 }, // D Modell
        { width: 20 }, // E Seriennummer
        { width: 18 }, // F CPU
        { width: 18 }, // G Hersteller
        { width: 14 }, // H RAM
        { width: 18 }, // I Speicher
        { width: 20 }, // J Betriebssystem

        { width: 22 }, // K Buch Versand
        { width: 16 }, // L Startdatum
        { width: 16 }, // M Enddatum

        { width: 20 }, // N Standort
        { width: 20 }, // O Asset-Tag
        { width: 18 }, // P Status
        { width: 36 }, // Q Anmerkungen

        { width: 22 }, // R Rückgabestatus

        { width: 22 }, // S Admin-Benutzername
        { width: 22 }, // T Admin-Passwort
        { width: 22 }, // U Passwort
      ];

      // ==========================
      // LISTS SHEET
      // ==========================
      const lists = wb.addWorksheet('Lists');
      lists.state = 'hidden';

      lists.getCell('A1').value = 'Kategorien';
      this.categories.forEach(
        (c, i) => (lists.getCell(`A${i + 2}`).value = c.name),
      );

      lists.getCell('B1').value = 'Kurse';
      this.courses.forEach(
        (c, i) => (lists.getCell(`B${i + 2}`).value = c.courseName),
      );

      lists.getCell('C1').value = 'Teilnehmer';
      this.participants.forEach(
        (p, i) => (lists.getCell(`C${i + 2}`).value = p.fullName),
      );

      lists.getCell('D1').value = 'Seriennummern';
      (this.existingSerials ?? []).forEach(
        (sn, i) => (lists.getCell(`D${i + 2}`).value = sn),
      );

      // Status kommt aus inventory_statuses, damit hier keine zweite Liste driftet
      lists.getCell('E1').value = 'StatusOptions';
      this.statuses.forEach((st, i) => {
        lists.getCell(`E${i + 2}`).value = st.name;
      });

      // Rückgabestatus options
      lists.getCell('F1').value = 'RueckgabeStatusOptions';
      ['Nicht zurückgegeben', 'Zurückgegeben'].forEach((v, i) => {
        lists.getCell(`F${i + 2}`).value = v;
      });

      //  Buch Versand options
      lists.getCell('G1').value = 'BuchVersandOptions';
      lists.getCell('G2').value = 'Versendet';
      lists.getCell('G3').value = 'Nicht versendet';

      const catEnd = Math.max(2, this.categories.length + 1);
      const courseEnd = Math.max(2, this.courses.length + 1);
      const participantEnd = Math.max(2, this.participants.length + 1);
      const serialEnd = Math.max(2, (this.existingSerials?.length ?? 0) + 1);
      const statusEnd = Math.max(2, this.statuses.length + 1);

      ws.getColumn(12).numFmt = '@'; // L Startdatum
      ws.getColumn(13).numFmt = '@'; // M Enddatum

      const TEMPLATE_ROWS = 500;

      // Dropdown validations (A-C)
      for (let r = 3; r <= TEMPLATE_ROWS + 2; r++) {
        ws.getCell(`A${r}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: [`=Lists!$A$2:$A$${catEnd}`],
          showInputMessage: true,
          promptTitle: 'Dropdown',
          prompt: 'Kategorie bitte aus der Dropdown-Liste auswählen.',
        };

        ws.getCell(`B${r}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: [`=Lists!$B$2:$B$${courseEnd}`],
          showInputMessage: true,
          promptTitle: 'Dropdown',
          prompt: 'Kurs bitte aus der Dropdown-Liste auswählen.',
        };

        ws.getCell(`C${r}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: [`=Lists!$C$2:$C$${participantEnd}`],
          showInputMessage: true,
          promptTitle: 'Dropdown',
          prompt: 'Teilnehmer bitte aus der Dropdown-Liste auswählen.',
        };
      }

      // Seriennummer validation
      const firstUserRow = 4;
      const lastUserRow = TEMPLATE_ROWS + 2;

      for (let r = firstUserRow; r <= lastUserRow; r++) {
        ws.getCell(`E${r}`).dataValidation = {
          type: 'custom',
          allowBlank: true,
          formulae: [
            `=OR(E${r}="",COUNTIF(Lists!$D$2:$D$${serialEnd},E${r})=0)`,
          ],
          showErrorMessage: true,
          errorStyle: 'warning',
          errorTitle: 'Seriennummer bereits vorhanden',
          error:
            'Diese Seriennummer existiert bereits im System. Bitte eine andere Seriennummer verwenden.',
        };
      }

      // Status dropdown
      for (let r = 3; r <= TEMPLATE_ROWS + 2; r++) {
        ws.getCell(`P${r}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [`=Lists!$E$2:$E$${statusEnd}`],
          showInputMessage: true,
          promptTitle: 'Status',
          prompt: 'Bitte einen Status auswählen.',
        };
      }

      // Rückgabestatus dropdown
      for (let r = 3; r <= TEMPLATE_ROWS + 2; r++) {
        ws.getCell(`R${r}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: ['=Lists!$F$2:$F$3'],
          showInputMessage: true,
          promptTitle: 'Rückgabestatus',
          prompt: 'Bitte Rückgabestatus auswählen.',
        };
      }

      //  Buch Versand dropdown
      for (let r = 3; r <= TEMPLATE_ROWS + 2; r++) {
        ws.getCell(`K${r}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: ['=Lists!$G$2:$G$3'],
          showInputMessage: true,
          promptTitle: 'Buch Versand',
          prompt: 'Bitte auswählen: Versendet / Nicht versendet.',
        };
      }

      // Beispielzeile
      ws.getCell('A3').value = this.categories[0]?.name ?? '';
      ws.getCell('B3').value = this.courses[0]?.courseName ?? '';
      ws.getCell('C3').value = this.participants[0]?.fullName ?? '';

      ws.getCell('D3').value = 'Acer (BEISPIEL)';
      ws.getCell('E3').value = 'SN-BEISPIEL-12345';
      ws.getCell('F3').value = 'Intel i5 (BEISPIEL)';
      ws.getCell('G3').value = 'Dell';
      ws.getCell('H3').value = '16 GB';
      ws.getCell('I3').value = '512 GB SSD';
      ws.getCell('J3').value = 'Windows 11';

      ws.getCell('K3').value = 'Versendet';

      ws.getCell('L3').value = '2026-01-06';
      ws.getCell('M3').value = '2026-01-09';

      ws.getCell('N3').value = 'Raum 2.15';
      ws.getCell('O3').value = 'ASSET-2026-001';

      ws.getCell('P3').value = this.statuses[0]?.name ?? '';
      ws.getCell('Q3').value = 'BEISPIEL – bitte eigene Daten eintragen.';

      ws.getCell('R3').value = 'Zurückgegeben';

      ws.getCell('S3').value = 'Admin';
      ws.getCell('T3').value = 'AdminPW';
      ws.getCell('U3').value = 'UserPW';

      ws.getRow(3).eachCell((cell: any) => {
        cell.font = { italic: true, color: { argb: 'FF6B7280' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFF7ED' },
        };
      });

      const COLS = headers.length;
      for (let r = 3; r <= TEMPLATE_ROWS + 2; r++) {
        for (let c = 1; c <= COLS; c++) {
          ws.getCell(r, c).protection = { locked: false };
        }
      }

      await ws.protect('readonly-example', {
        selectLockedCells: true,
        selectUnlockedCells: true,
      });

      const buffer = await wb.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        'inventar-import-vorlage.xlsx',
      );
    } catch (e: any) {
      this.validationErrors = [
        `Vorlage konnte nicht erstellt werden: ${e?.message ?? e}`,
      ];
    } finally {
      this.loadingTemplate = false;
    }
  }

  // ==========================
  // FILE HANDLE
  // ==========================
  private async handleFile(file: File): Promise<void> {
    this.processingFile = true;
    this.selectedFile = file;
    this.validationErrors = [];
    this.excelRows = [];
    this.payload = [];
    this.fileName = file.name;

    const reader = new FileReader();
    reader.onload = async (e: any) => {
      try {
        await this.loadListsFromDb();

        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];

        const raw: ImportRowDE[] = XLSX.utils.sheet_to_json(ws, {
          defval: '',
          range: 1,
        });

        const withMeta: ImportRowDEWithMeta[] = raw.map((r, idx) => ({
          ...r,
          __rowNo: idx + 3,
        }));

        const cleaned = withMeta.filter((r) => !this.isExampleRow(r));

        this.excelRows = cleaned;

        await this.buildPayloadFromExcel(cleaned);
      } catch (err: any) {
        this.validationErrors = [
          `Datei konnte nicht verarbeitet werden: ${err?.message ?? err}`,
        ];
      } finally {
        this.processingFile = false;
      }
    };

    reader.onerror = () => {
      this.processingFile = false;
      this.validationErrors = ['Die Datei konnte nicht gelesen werden.'];
    };

    reader.readAsArrayBuffer(file);
  }

  private async buildPayloadFromExcel(
    rows: ImportRowDEWithMeta[],
  ): Promise<void> {
    const errors: string[] = [];

    // ==========================
    // MAPPINGS
    // ==========================
    const catMap = new Map<string, number>();
    const kindMap = new Map<string, CategoryKind>();
    this.categories.forEach((c) => {
      const key = c.name.trim().toLowerCase();
      catMap.set(key, c.id);
      kindMap.set(key, c.kind);
    });

    const courseMap = new Map<string, number>();
    this.courses.forEach((c) =>
      courseMap.set(c.courseName.trim().toLowerCase(), c.courseId),
    );

    const participantMap = new Map<string, number[]>();
    this.participants.forEach((p) => {
      const key = (p.fullName ?? '').trim();
      if (!key) return;
      const arr = participantMap.get(key) ?? [];
      arr.push(p.id);
      participantMap.set(key, arr);
    });

    // ==========================
    // PAYLOAD BUILD
    // ==========================
    const payload: CreateInventoryDto[] = [];

    rows.forEach((r) => {
      const hasAnyValue =
        r.Kategorie ||
        r.Kurs ||
        r.Teilnehmer ||
        r.Seriennummer ||
        r.Modell ||
        r['Buch Versand'];

      if (!hasAnyValue) return;

      const rowNo = r.__rowNo;

      // Beispielzeile überspringen
      const desc = (r.Anmerkungen ?? '').toString().trim().toUpperCase();
      if (desc.includes('BEISPIEL')) return;

      const categoryName = (r.Kategorie ?? '').toString().trim().toLowerCase();
      const courseName = (r.Kurs ?? '').toString().trim().toLowerCase();
      const participantNameRaw = (r.Teilnehmer ?? '').toString().trim();

      const categoryId = catMap.get(categoryName) ?? null;
      const courseId = courseMap.get(courseName) ?? null;
      const serial = this.nullIfEmpty(r.Seriennummer);
      const kind = kindMap.get(categoryName) ?? 'TECH';

      if (!categoryId)
        errors.push(
          `Zeile ${rowNo}: Kategorie nicht gefunden ("${r.Kategorie}")`,
        );
      if (!courseId)
        errors.push(`Zeile ${rowNo}: Kurs nicht gefunden ("${r.Kurs}")`);

      // Buecher tragen keine Seriennummer.
      if (kind === 'TECH' && !serial)
        errors.push(`Zeile ${rowNo}: Seriennummer fehlt`);

      const matches = participantMap.get(participantNameRaw) ?? [];
      let participantId: number | null = null;

      if (!participantNameRaw) {
        errors.push(`Zeile ${rowNo}: Teilnehmer fehlt`);
      } else if (matches.length === 0) {
        errors.push(
          `Zeile ${rowNo}: Teilnehmer nicht gefunden ("${participantNameRaw}")`,
        );
      } else if (matches.length > 1) {
        errors.push(
          `Zeile ${rowNo}: Teilnehmername ist nicht eindeutig ("${participantNameRaw}")`,
        );
      } else {
        participantId = matches[0];
      }

      if (!categoryId) return;

      const buchVersandRaw = (r['Buch Versand'] ?? '')
        .toString()
        .trim()
        .toLowerCase();
      let buchVersandStatus: string | null = null;

      if (kind === 'BOOK') {
        if (buchVersandRaw === 'versendet') buchVersandStatus = 'Versendet';
        if (buchVersandRaw === 'nicht versendet')
          buchVersandStatus = 'Nicht versendet';
      }

      // Die Vorlage fuehrt Anzeigenamen, gespeichert wird der Code aus inventory_statuses.
      const statusRaw = this.nullIfEmpty(r.Status);
      const statusCode = this.resolveStatusCode(statusRaw);
      if (statusRaw && !statusCode)
        errors.push(`Zeile ${rowNo}: Status nicht gefunden ("${statusRaw}")`);

      const isBook = kind === 'BOOK';

      payload.push({
        categoryId,
        courseId,
        participantId,

        // Bei Buechern traegt model den Buchtitel.
        model: this.nullIfEmpty(r.Modell),
        serialNumber: isBook ? null : serial,

        cpu: isBook ? null : this.nullIfEmpty(r.CPU),
        hersteller: isBook ? null : this.nullIfEmpty(r.Hersteller),
        ram: isBook ? null : this.nullIfEmpty(r.RAM),
        speicher: isBook ? null : this.nullIfEmpty(r.Speicher),
        betriebssystem: isBook ? null : this.nullIfEmpty(r.Betriebssystem),

        buchVersandStatus,

        startDate: this.nullIfEmpty(r.Startdatum),
        endDate: this.nullIfEmpty(r.Enddatum),

        location: this.nullIfEmpty(r.Standort),
        assetTag: isBook ? null : this.nullIfEmpty(r['Asset-Tag']),

        status: statusCode,

        returned:
          (r.Rückgabestatus ?? '').toString().trim().toLowerCase() ===
          'zurückgegeben',

        adminBenutzername: isBook
          ? null
          : this.nullIfEmpty(r['Admin-Benutzername']),
        adminPasswort: isBook ? null : this.nullIfEmpty(r['Admin-Passwort']),
        passwort: isBook ? null : this.nullIfEmpty(r.Passwort),

        note: this.nullIfEmpty(r.Anmerkungen),
      });
    });

    // ==========================
    // DUPLICATES IN FILE
    // ==========================
    const seen = new Set<string>();
    payload.forEach((p) => {
      const key = (p.serialNumber ?? '').toLowerCase();
      if (!key) return;
      if (seen.has(key))
        errors.push(`Duplicate Seriennummer in Datei: "${p.serialNumber}"`);
      seen.add(key);
    });

    // ==========================
    // DUPLICATES IN DB
    // ==========================
    const existingSerialsLower = (this.existingSerials ?? []).map((s) =>
      s.toLowerCase(),
    );
    payload.forEach((p) => {
      const sn = (p.serialNumber ?? '').toLowerCase();
      if (sn && existingSerialsLower.includes(sn)) {
        errors.push(
          `Seriennummer existiert bereits im System (${p.serialNumber})`,
        );
      }
    });

    // FINAL STATE
    this.payload = payload;
    this.validationErrors = errors;
  }

  import(): void {
    if (!this.selectedFile) {
      this.notification.warn('Bitte zuerst eine Excel-Datei auswählen.');
      return;
    }

    // Ein teilweise fehlerhafter Payload wird nicht abgeschickt.
    if (this.validationErrors.length) {
      this.notification.warn(
        `Die Datei enthält ${this.validationErrors.length} Fehler. Bitte zuerst korrigieren.`,
      );
      return;
    }

    if (!this.payload.length) {
      this.notification.warn(
        'Die Excel-Datei enthält keine gültigen Datensätze zum Import.',
      );
      return;
    }

    this.importing = true;

    this.inventoryService
      .bulkCreateWithFile(this.selectedFile, this.payload)
      .subscribe({
        next: (res: any) => {
          this.importing = false;

          this.ref.close({
            imported: res?.imported ?? this.payload.length,
            file: res?.file ?? null,
          });
        },
        error: (err) => {
          this.importing = false;

          if (err?.status === 409) {
            const message =
              err?.error?.message ?? 'Einige Seriennummern existieren bereits.';
            const duplicates: string[] = Array.isArray(err?.error?.duplicates)
              ? err.error.duplicates
              : [];

            this.notification.warn(message);

            this.validationErrors = duplicates.map(
              (d) => `Seriennummer existiert bereits im System (${d})`,
            );

            return;
          }

          this.notification.error(
            err?.error?.message ?? 'Der Import ist fehlgeschlagen.',
            err,
          );
        },
      });
  }

  private async loadListsFromDb(): Promise<void> {
    const cats = await firstValueFrom(
      this.inventoryService.getCategories().pipe(take(1)),
    );
    this.categories = (cats ?? []).map((c: any) => ({
      id: c.id ?? c.categoryId,
      name: (c.name ?? '').toString().trim(),
      kind: parseCategoryKind(c.kind),
    }));

    const courses = await firstValueFrom(
      this.courseService.getCourseObservable().pipe(take(1)),
    );
    this.courses = (courses ?? []).map((c: any) => ({
      courseId: c.courseId ?? c.id,
      courseName: (c.courseName ?? c.name ?? '').toString().trim(),
    }));

    const users = await firstValueFrom(
      this.userService.getUsersObservable().pipe(take(1)),
    );
    this.participants = (users ?? [])
      .map((u: any) => ({
        id: u.userId ?? u.id,
        fullName: (
          u.fullName ?? `${u.firstname ?? ''} ${u.lastname ?? ''}`
        ).trim(),
      }))
      .filter((p: any) => !!p.fullName);

    const statuses = await firstValueFrom(
      this.inventoryService.getInventoryStatuses().pipe(take(1)),
    );
    this.statuses = (statuses ?? []).map((s: any) => ({
      code: (s.code ?? '').toString().trim(),
      name: (s.name ?? '').toString().trim(),
    }));

    const serials = await firstValueFrom(
      this.inventoryService.getSerialNumbers().pipe(take(1)),
    );
    this.existingSerials = (serials ?? [])
      .map((s: any) => (s ?? '').toString().trim())
      .filter((s: string) => s.length > 1);
  }

  private resolveStatusCode(raw: string | null): string | null {
    if (!raw) return null;

    const key = raw.trim().toLowerCase();
    const hit = this.statuses.find(
      (s) => s.name.toLowerCase() === key || s.code.toLowerCase() === key,
    );

    return hit ? hit.code : null;
  }

  private nullIfEmpty(v: any): string | null {
    const s = (v ?? '').toString().trim();
    return s ? s : null;
  }
}
